import { useEffect, useState } from 'react'
import { AlertTriangle, ArrowRight, Check, X } from 'lucide-react'
import { formatDateTimeCompact } from '@/lib/datetime'
import { decimalInputToMinor, formatMinorCurrency } from '@/lib/format'
import { queryKeys } from '@/lib/query-client'
import type { AdminTripEdit } from '@/types/api'
import { useAdminMutation } from '@/hooks/useAdminMutation'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { KeyValue, KeyValueGrid } from '@/components/ui/RowActions'
import { AreaSurchargeDetails } from '@/features/area-pricing/AreaSurchargeBadge'
import { decideTripEdit } from './api'
import { diffOf, scheduleDrifted } from './describe'
import styles from './TripEditsPage.module.css'

interface ReviewTripEditDialogProps {
  edit: AdminTripEdit | null
  onClose: () => void
}

/**
 * The one decision this feature exists for: does the customer get their change,
 * and does it cost anything more?
 *
 * The dialog is built so the default is the safe one. The amount starts empty,
 * which means "keep the cost the same" — a supervisor who does not want to
 * charge anything types nothing and clicks approve. A figure is only ever there
 * because somebody put it there.
 *
 * Approving with a charge deliberately changes nothing on the booking. The
 * customer is asked to pay first and the change lands on the payment, so a
 * request approved and never paid for costs the office nothing to abandon. The
 * footer says so, because it is not what "Approve" normally implies.
 */
export function ReviewTripEditDialog({ edit, onClose }: ReviewTripEditDialogProps) {
  const open = edit !== null
  const [extra, setExtra] = useState('')
  const [note, setNote] = useState('')

  useEffect(() => {
    if (open) {
      setExtra('')
      setNote('')
    }
  }, [open, edit?.id])

  const extraMinor = extra.trim() === '' ? 0 : decimalInputToMinor(extra)
  const extraInvalid = extra.trim() !== '' && (extraMinor === null || extraMinor < 0)
  const charging = extraMinor !== null && extraMinor > 0

  const mutation = useAdminMutation({
    mutationFn: (approve: boolean) =>
      decideTripEdit(edit!.id, {
        approve,
        // Sent in EGP, which is what the supervisor typed. The server converts
        // once, so no browser has to know the scale of a piastre.
        extraAmount: approve && extraMinor ? extraMinor / 100 : undefined,
        note: note.trim() || undefined,
      }),
    successTitle: 'Decision recorded',
    successDescription: (updated) => {
      if (updated.status === 'REJECTED') {
        return 'The customer has been told, and their booking is unchanged.'
      }
      if (updated.status === 'AWAITING_PAYMENT') {
        return `The customer has been asked to pay ${formatMinorCurrency(
          updated.extraAmountMinor,
          updated.currency,
        )}. Their booking stays as it is until the money is in.`
      }
      return 'The booking has been updated and the customer notified.'
    },
    errorTitle: 'The decision was not recorded',
    invalidate: [
      queryKeys.tripEdits.all,
      queryKeys.tripRequests.all,
      queryKeys.trips.all,
      queryKeys.dashboard.all,
    ],
    onSuccess: () => onClose(),
  })

  const rows = edit ? diffOf(edit) : []
  const drifted = edit ? scheduleDrifted(edit) : false

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Review this change"
      size="lg"
      busy={mutation.isPending}
      description="Approve the change, with or without an extra charge, or decline it with a reason the customer will read."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Close
          </Button>
          <Button
            variant="danger"
            iconLeft={<X aria-hidden="true" />}
            disabled={mutation.isPending}
            onClick={() => mutation.mutate(false)}
          >
            Decline
          </Button>
          <Button
            variant="primary"
            iconLeft={<Check aria-hidden="true" />}
            loading={mutation.isPending}
            disabled={extraInvalid || mutation.isPending}
            onClick={() => mutation.mutate(true)}
          >
            {charging ? 'Approve and request payment' : 'Approve, same price'}
          </Button>
        </>
      }
    >
      {edit ? (
        <div className={styles.dialogBody}>
          <section className={styles.summary}>
            <KeyValueGrid>
              <KeyValue label="Customer">{edit.customerName ?? `#${edit.userId}`}</KeyValue>
              <KeyValue label="Phone">{edit.customerPhone ?? '—'}</KeyValue>
              <KeyValue label="Booking">
                <span className={styles.mono}>{edit.bookingReference ?? `#${edit.tripRequestId}`}</span>
              </KeyValue>
              <KeyValue label="Paid so far">
                {formatMinorCurrency(edit.bookingTotalMinor, edit.currency)}
              </KeyValue>
            </KeyValueGrid>
            {edit.customerNote ? <p className={styles.note}>“{edit.customerNote}”</p> : null}
          </section>

          {/* The change itself, before and after. This is what the supervisor is
              pricing, so it comes before the amount field rather than after. */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>What the customer asked for</h3>
            <ul className={styles.diffList}>
              {rows.map((row) => (
                <li key={`${row.kind}-${row.label}`} className={styles.diffRow}>
                  <span className={styles.diffLabel}>{row.label}</span>
                  <span className={styles.diffBefore}>{row.before}</span>
                  <ArrowRight aria-hidden="true" className={styles.diffArrow} />
                  <span className={styles.diffAfter}>{row.after}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* A new pickup or drop-off in Cairo can cross into a dearer group,
              and a new time or duration can move an end into the late-night
              window. The figures are the server's, at today's prices; nothing
              is charged for it unless the supervisor types an amount below. */}
          {edit.requestedAreaSurcharge ? (
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>Cairo area surcharge</h3>
              <KeyValueGrid>
                <KeyValue label="Booked with">
                  {edit.areaSurcharge.any ? (
                    <AreaSurchargeDetails area={edit.areaSurcharge} />
                  ) : (
                    'No area surcharge'
                  )}
                </KeyValue>
                <KeyValue
                  label={
                    edit.changes.length === 1 && edit.changes[0] === 'LOCATION'
                      ? 'At the new locations'
                      : 'With the change'
                  }
                >
                  {edit.requestedAreaSurcharge.any ? (
                    <AreaSurchargeDetails area={edit.requestedAreaSurcharge} />
                  ) : (
                    'No area surcharge'
                  )}
                </KeyValue>
              </KeyValueGrid>
              <p className={styles.areaHint}>
                Before VAT, at today&apos;s area and late-night prices. Approving does not change the fare by itself
                — enter an extra charge below if the customer should pay for the change.
              </p>
            </section>
          ) : null}

          {/* The office can move a booking while a request is queued. Saying so
              beats a supervisor approving a time change from a "before" that is
              no longer the booking. */}
          {drifted ? (
            <div className={styles.warning} role="note">
              <AlertTriangle aria-hidden="true" />
              <p>
                This booking has moved since the customer asked. It is now at{' '}
                <strong>{formatDateTimeCompact(edit.bookingStartTime)}</strong>, not{' '}
                {formatDateTimeCompact(edit.previousStartTime)}. Approving will set it to the time above.
              </p>
            </div>
          ) : null}

          <Field
            label="Extra charge (EGP)"
            error={extraInvalid ? 'Enter an amount like 150 or 150.00.' : undefined}
            hint={
              charging
                ? 'The booking will not change until the customer pays this. They can pay by card or Instapay.'
                : 'Leave empty to approve at the price the booking already carries.'
            }
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                inputMode="decimal"
                value={extra}
                placeholder="0.00"
                disabled={mutation.isPending}
                onChange={(event) => setExtra(event.target.value)}
              />
            )}
          </Field>

          <Field
            label="Note for the customer"
            hint="Shown in their app. A declined change with no reason is the thing that generates a phone call."
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                value={note}
                maxLength={500}
                disabled={mutation.isPending}
                placeholder="Later slot is available, but the driver has to travel further…"
                onChange={(event) => setNote(event.target.value)}
              />
            )}
          </Field>
        </div>
      ) : null}
    </Dialog>
  )
}
